import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, firstValueFrom, interval, Observable, of, Subject} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError, filter, map, pluck, tap} from 'rxjs/operators';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import {environment} from '../../environments/environment';
import {cloneDeep, get, intersection, isArray} from 'lodash-es';

export const parseJwt = (token): any => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('')
    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
  return JSON.parse(jsonPayload);
};

const menus = [
  {
    link: '/resident/lav-occupancy',
    icon: 'hdd',
    title: 'Lavatory Status (Admin)',
    eligible: {
      roles: ['admin'],
    },
  },
  {
    link: '/resident/pre-check-in',
    icon: 'audit',
    title: 'Pre Check-In',
    eligible: {
      compound: ['MOS'],
    }
  },
  {
    link: '/resident/scam-simulation',
    icon: 'reddit',
    title: 'Scam Simulation Bot',
    subTitle: '(Anti-Scam Lab)',
    eligible: {
      compound: ['MOS'],
      halls: ['SR14'],
      'extra.ascd_code': ['CI'],
      'extra.nt': ['M']
    }
  },
  {
    link: '/resident/pre-check-in-klnt',
    icon: 'audit',
    title: 'Pre Check-In',
    eligible: {
      compound: ['KLT', 'NSY'],
    }
  },
  {
    link: '/resident/service-request',
    icon: 'tool',
    title: 'Service Request',
    eligible: {
      compound: ['MOS', 'KLT'],
      'extra.ascd_code': ['CI']
    }
  },

  {
    link: '/resident/laundry-status',
    icon: 'field-time',
    title: 'Laundry Status',
    eligible: {
      compound: ['MOS', 'KLT'],
      'extra.ascd_code': ['CI']
    }
  },
  {
    link: '/resident/visitor-registration',
    icon: 'form',
    title: 'Visitor Registration',
    eligible: {
      compound: ['MOS'],
      'extra.ascd_code': ['CI']
    }
  },
  {
    link: '/resident/sports-hall',
    icon: 'carry-out',
    title: 'Sports Hall',
    eligible: {
      compound: ['MOS'],
      'extra.ascd_code': ['CI']
    }
  },
  {
    icon: 'swap',
    link: '/resident/room-moving',
    title: 'Room Moving',
    eligible: {
      compound: ['MOS'],
      'extra.ascd_code': ['CI','AS']
    }
  },
  {
    icon: 'node-index',
    link: '/resident/bicycle-lot',
    title: 'Bicycle Parking Facilities',
    eligible: {
      compound: ['MOS'],
      'extra.ascd_code': ['CI']
    }
  },
  {
    link: '/resident/feedback',
    icon: 'sound',
    title: 'Feedback',
    eligible: {
      compound: ['MOS', 'KLT'],
      'extra.ascd_code': ['CI']
    }
  },

];

export function tokenToUser(domain: string, token: string): User {
  const claims = parseJwt(token);
  claims.exp = claims.exp * 1000;
  const menusClone = cloneDeep(menus);
  const menuItems = menusClone.filter(m => {
    const eligible = m.eligible;
    const user = claims.user;
    const failure = [];
    Object.keys(eligible).forEach(key => {
      const userValue = get(user, key);
      const valid = intersection(eligible[key], isArray(userValue) ? userValue : [userValue]);
      if (valid.length < 1) {
        failure.push({
          key: key,
          allowed_value: eligible[key],
          user: userValue
        });
      }
    });
    return !(failure.length > 0);
  });
  return {
    ...claims.user,
    exp: new Date(claims.exp),
    menuItems: menuItems
  };
}

export function authHeader(token: string): any {
  return {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    })
  };
}

export interface User {
  name: string;
  uid: string;
  role: string;
  email: string;
  halls: string[];
  exp: Date;
  roles: string[];
  extra?: any;
  menuItems?: any[];
  compound?: any;
}

export const REFRESH_TOKEN_KEY = 'NGX-STUD_PORTAL-AUTH_REFRESH_TOKEN';
export const ACCESS_TOKEN_KEY = 'NGX-STUD_PORTAL-AUTH_ACCESS_TOKEN';


export const get_domain = (): string => {
  const hostname = window.location.hostname;
  if (hostname.startsWith('mos-portal') || hostname.startsWith('localhost')) {
    return 'MOS';
  }
  if (hostname.startsWith('klnt-portal')) {
    return 'KLT';
  }
  return 'MOS';
};

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  public tokenRenew$ = new Subject();
  private domain = get_domain();
  // TODO - error handling in sign-in flow
  private user$ = new BehaviorSubject<User>(null);
  private authApi = `./auth-api-${environment.production ? 'master' : 'staging'}`;

  constructor(private router: Router, private http: HttpClient) {
    document.title = `${this.domain} Portal [v${environment.version}]`;
    this.loadUserFromSessionToken();
    this.liveRenew();
  }

  user(): User {
    return this.user$.getValue();
  }

  getUser$(): Observable<User> {
    return this.user$.asObservable();
  }

  getRefreshToken(): string {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  getAccessToken(): string {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  async impersonate(token: string, redirect = ['menu']) {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    this.loadUserFromSessionToken();
    await this.router.navigate(redirect);
  }

  ssoSignIn(token: string, redirect = ['menu']): void {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    this.loadUserFromSessionToken();
    this.http.get(`${this.authApi}/obtain-refresh-token`, authHeader(token))
      .pipe(pluck('refresh_token'))
      .subscribe((t: string) => {
        sessionStorage.setItem(REFRESH_TOKEN_KEY, t);
        this.router.navigate(redirect).then();
      });
  }

  renewToken(uid: string): Observable<any> {
    const token = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    return this.http.post(`${this.authApi}/renew`, {
      userId: uid, refresh_token: token
    }).pipe(
      pluck('access_token'),
      tap((t: string) => sessionStorage.setItem(ACCESS_TOKEN_KEY, t)),
      tap(() => this.tokenRenew$.next({})),
      map((t: string) => tokenToUser(this.domain, t)),
      catchError(() => {
        this.signOut().then();
        return of({});
      }),
      tap((u: User) => this.user$.next(u)),
    );
  }

  async signOut(path = '/login'): Promise<void> {
    if (this.user$.getValue()?.uid) {
      await firstValueFrom(this.http.post(`${this.authApi}/sign-out`, {
        userId: this.user$.getValue().uid,
        refresh_token: this.getRefreshToken()
      }, authHeader(this.getAccessToken())));
      this.user$.next({} as User);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
      await this.router.navigateByUrl(path);
    }
  }

  isLoggedIn(): boolean {
    return !!this.user$.getValue()?.uid;
  }

  private loadUserFromSessionToken(): void {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      this.user$.next({} as User);
      return;
    }
    try {
      const user = tokenToUser(this.domain, token);
      if (user.exp.getTime() < (new Date()).getTime()) {
        this.renewToken(user.uid).subscribe();
      } else {
        this.user$.next(user);
      }
    } catch (e) {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  }

  private liveRenew(): void {
    interval(60 * 1000).pipe(
      filter(() => this.isLoggedIn()),
      tap(() => {
        if (!!this.user() && !!this.user().exp) {
          const diffInMin = differenceInMinutes(this.user().exp.getTime(), (new Date()).getTime());
          if (diffInMin < 5) {
            this.renewToken(this.user().uid).subscribe();
          }
        } else {
          this.signOut().then();
        }
      })
    ).subscribe();
  }

}
